import { useState } from 'react';
import { SoldProduct, PaginatedResponse, Product, Client, Appointment, Discount } from '../types/entities';
import { ApiError } from '../types/api';
import { SoldProductFormData } from '../types/forms';
import { getAuthenticatedFetch } from '../utils/api';
import { useErrorHandler } from '../utils/errorHandler';

interface SalesQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  client_id?: number;
  appointment_id?: number;
  product_id?: number;
}

export const useSalesApi = () => {
  const [error, setError] = useState<ApiError | null>(null);
  const { handleApiError: handleError } = useErrorHandler();

  const handleApiError = (err: any): ApiError => {
    const apiError = handleError(err);
    setError(apiError);
    return apiError;
  };

  const clearError = () => setError(null);

  const fetchSales = async (queryParams?: SalesQueryParams): Promise<PaginatedResponse<SoldProduct>> => {
    try {
      clearError();
      const queryString = queryParams ? new URLSearchParams(
        Object.entries(queryParams).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = value.toString();
          }
          return acc;
        }, {} as Record<string, string>)
      ).toString() : '';
      
      const url = queryString ? `/products-sell?${queryString}` : `/products-sell`;
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(url);
      
      const data: PaginatedResponse<SoldProduct> = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const fetchSale = async (id: number): Promise<SoldProduct> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/products-sell/${id}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const createSale = async (saleData: SoldProductFormData): Promise<SoldProduct> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/products-sell`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(saleData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const updateSale = async (id: number, saleData: Partial<SoldProductFormData>): Promise<SoldProduct> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/products-sell/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(saleData),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (err) {
      throw handleApiError(err);
    }
  };

  const deleteSale = async (id: number): Promise<void> => {
    try {
      clearError();
      const authenticatedFetch = getAuthenticatedFetch();
      const response = await authenticatedFetch(`/products-sell/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (err) {
      throw handleApiError(err);
    }
  };

  return {
    error,
    clearError,
    fetchSales,
    fetchSale,
    createSale,
    updateSale,
    deleteSale,
  };
};





